import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@analyticsMD/quil";
import { Loading } from "@analyticsMD/quil";
import { Sizes } from "../types/loadingSizes";
import FHIRContext from "../contexts/FHIRContext";
import ErrorPage from "../components/error-page";
import { useEmrHook } from "../hooks/use-emr-hook";
import { getCachedJwksUri, getCachedIssuer } from "../utils/fhir-cache-patch";

const AuthRedirectPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const { client, error: fhirError } = useContext(FHIRContext);
  const navigate = useNavigate();
  const { emrContext, error: emrError } = useEmrHook(client);

  // Function to redirect to root with query parameters
  const redirectToRoot = useCallback(
    (emrContext) => {
      try {
        const queryParams = new URLSearchParams();

        // Add launch token values
        Object.keys(emrContext).forEach((key) => {
          queryParams.set(key, emrContext[key]);
        });

        // Add FHIR launch context data
        const issuer = getCachedIssuer();
        queryParams.set("iss", issuer);
        const jwksUri = getCachedJwksUri();
        queryParams.set("jwks_uri", jwksUri);
        const redirectUrl = `/?${queryParams.toString()}`;
        console.log("🔄 Redirecting to:", redirectUrl);

        // Navigate to root with query parameters
        navigate(redirectUrl);
      } catch (redirectError) {
        setError(
          redirectError instanceof Error
            ? redirectError.message
            : String(redirectError)
        );
      }
    },
    [navigate, setError]
  );

  useEffect(() => {
    if (emrContext) {
      // Check if we have all required identifiers
      const hasAllRequiredIds =
        emrContext.emr_user_id &&
        emrContext.emr_patient_id &&
        emrContext.emr_hospital_id &&
        emrContext.fhir_patient_id &&
        emrContext.id_token;

      if (hasAllRequiredIds) {
        redirectToRoot(emrContext);
      } else {
        const missingIdentifiers = {
          emr_user_id: !!emrContext.emr_user_id,
          emr_patient_id: !!emrContext.emr_patient_id,
          emr_hospital_id: !!emrContext.emr_hospital_id,
          fhir_patient_id: !!emrContext.fhir_patient_id,
          id_token: !!emrContext.id_token,
        };
        setError(
          `Missing required identifiers: ${JSON.stringify(missingIdentifiers)}`
        );
      }
    }
  }, [emrContext, navigate, redirectToRoot]);

  // Error state
  if (error || fhirError || emrError) {
    return (
      <>
        <ErrorPage
          error={
            new Error(
              error || fhirError || emrError || "Failed to load FHIR client"
            )
          }
        />
      </>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <Box sx={{ marginTop: "25%", height: "100%" }}>
        <Loading message={"Loading..."} logoSize={Sizes.Medium} />
      </Box>
    </Box>
  );
};

export default AuthRedirectPage;
